import {
    defaultBaseQuery,
    transformErrorResponse,
    transformSuccessResponse,
    transformRequest,
    buildFormData
} from '@/services';
import { createApi } from '@reduxjs/toolkit/query/react';

const placementApi = createApi({
    baseQuery: defaultBaseQuery,
    reducerPath: 'placementApi',
    endpoints: (builder) => ({

        // Manage Job Posting - list
        getPlacementJobList: builder.query({
            query: ({
                page = 0, size = 10, searchText = '', sortBy = '', sortDir = '',
                programId = '', branchId = '', jobTypeId = '', stateId = '', districtId = ''
            } = {}) => ({
                url: `/placements/job-posting`,
                method: 'GET',
                params: {
                    page, size, ...(searchText && { searchText }),
                    ...(sortBy && { sortBy }), ...(sortDir && { sortDir }),
                    ...(programId && { programId }), ...(branchId && { branchId }),
                    ...(jobTypeId && { jobTypeId }), ...(stateId && { stateId }),
                    ...(districtId && { districtId })
                }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getPlacementJobById: builder.query({
            query: (id) => ({
                url: `/placements/job-posting/${id}`,
                method: 'GET'
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getStudentApplicationsByPostId: builder.query({
            // ...filters: the Student View filters (universityId, stateId, semYearId, ...)
            query: ({ postId, page = 0, size = 10, searchText = '', ...filters } = {}) => ({
                url: `/placements/application/post/${postId}`,
                method: 'GET',
                params: { page, size, ...filters, ...(searchText && { searchText }) }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        // View Student Application — the applicant's own details, picked by their uskId
        getApplicantRegisterInfo: builder.query({
            query: (uskId) => ({
                url: '/sreg/student/register',
                method: 'GET',
                params: { uskId }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getApplicantCertificates: builder.query({
            query: (uskId) => ({
                url: '/ssp/student/certificates',
                method: 'GET',
                params: { uskId }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        // View Student Application — the resume submitted with this application
        getApplicationStudentResume: builder.query({
            query: (applicationId) => ({
                url: `/placements/application/${applicationId}/student-resume`,
                method: 'GET'
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getApplicantJobSkills: builder.query({
            query: (uskId) => ({
                url: '/ssp/student/skills/job-skills',
                method: 'GET',
                params: { uskId }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        getStudentApplicationFlow: builder.query({
            query: ({ page = 0, size = 10 } = {}) => ({
                url: `/placements/student/applications/`,
                method: 'GET',
                params: { page, size }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        // data = the status master item { id, value: "Rejected", parentId } (+ optional remark)
        // → body { id, value: id, parentId, label: "Rejected" }
        updateApplicationStatus: builder.mutation({
            query: ({ applicationId, postId, data }) => ({
                url: `/placements/application/${applicationId}/post/${postId}/status`,
                method: 'PATCH',
                body: transformRequest({
                    id: data.id,
                    value: data.id,
                    parentId: data.parentId,
                    label: data.value,
                    ...(data.remark && { remark: data.remark }),
                })
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        publishPlacementJob: builder.mutation({
            query: (id) => ({
                url: `/placements/job-posting/${id}/publish`,
                method: 'PATCH'
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        deletePlacementJob: builder.mutation({
            query: (id) => ({
                url: `/placements/job-posting/${id}`,
                method: 'DELETE'
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        // Job location dropdown - the logged-in company's own office locations
        getJobPostingLocations: builder.query({
            query: () => ({
                url: `/placements/job-posting/locations`,
                method: 'GET'
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        // Add / Edit Job Posting (multipart: JSON "data" + optional JD "document")
        savePlacementJob: builder.mutation({
            query: ({ data, document }) => ({
                url: '/placements/job-posting',
                method: 'POST',
                body: buildFormData({ data: JSON.stringify(data), document })
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        updatePlacementJob: builder.mutation({
            query: ({ placementId, data, document }) => ({
                url: `/placements/job-posting/${placementId}`,
                method: 'PUT',
                body: buildFormData({ data: JSON.stringify(data), document })
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),
    }),
});

export const {
    useLazyGetPlacementJobListQuery,
    useLazyGetPlacementJobByIdQuery,
    useLazyGetStudentApplicationsByPostIdQuery,
    useLazyGetApplicantRegisterInfoQuery,
    useLazyGetApplicantCertificatesQuery,
    useLazyGetApplicantJobSkillsQuery,
    useLazyGetApplicationStudentResumeQuery,
    useLazyGetStudentApplicationFlowQuery,
    useUpdateApplicationStatusMutation,
    usePublishPlacementJobMutation,
    useDeletePlacementJobMutation,
    useLazyGetJobPostingLocationsQuery,
    useSavePlacementJobMutation,
    useUpdatePlacementJobMutation,
} = placementApi;

export default placementApi;
